import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Folder from "../../../common/Folder/Folder";
import CreateFolderModel from "../../../../pages/HRM/Pages/Worker/Documents/CreateFolderModel";
import UploadNewFileModel from "../../../../pages/HRM/Pages/Worker/Documents/UploadNewFileModel";
import BulkUploadDocumentModal from "../../../../pages/HRM/Pages/Worker/Documents/BulkUploadDocumentModal";
import EditModel from "../../../../pages/HRM/Pages/Worker/Documents/EditModel";
import BreadCrumb from "../../../../pages/HRM/Pages/Worker/Documents/BreadCrumb";
import CustomButton from "../../../common/Button/CustomButton";
import DocumentsHeader from "./DocumentsHeader";
import FilePreviewModal from "./FilePreviewModal";
import RecentFiles from "./RecentFiles";
import ItemMetadata from "./ItemMetadata";
import DocumentSkeleton from "./DocumentSkeleton";
import CheckBox from "../../../common/checkBox/checkbox";
import { BsUpload, BsFiles, BsFolderPlus } from "react-icons/bs";
import { ChevronRightIcon, TrashIcon, FolderIcon } from "@heroicons/react/outline";
import {
  canAccessDocumentAction,
  canParticipantDocumentFileDelete,
  canParticipantDocumentFolderDelete,
  DOCUMENT_PERMISSION_ACTIONS,
  PARTICIPANT_DOCUMENT_FOLDER_DELETE_DENIED,
  isParticipantFolderDeleteDeniedError,
  roleAccess,
  usePermissionsVersion,
} from "../../../../helpers/RolesPermission";
import { USER_ROLE } from "../../../../Constants/constant";
import {
  PERMISSION_KEYS,
  PERMISSION_KEYS_CHILDREN,
} from "../../../../store/constants";
import moment from "moment";
import {
  isDocumentFileExpired,
  isDocumentFileExpiringSoon,
} from "../../../../Utils/utils";
import { toast } from "react-toastify";
import ConfirmationModal from "../../../common/Modal/ConfirmationModal";
import CircularLoader from "../../../common/CircularLoader/CircularLoader";
import MoveToParticipantFolderModal, {
  executeMoveToFolder,
  MOVE_ALREADY_IN_DESTINATION,
} from "./MoveToParticipantFolderModal";
import WorkerDocument from "../../../../pages/HRM/Pages/Worker/Documents/DocumentsPage";
import Folderr from "../../../../assets/image14.svg";
import { apiGetRequest } from "helpers/Requests";
import {
  formatFolderContentsLabel,
  parseBooleanVal,
  toPlainDocumentList,
} from "Utils/utils";
import {
  URL_SP_PARTICIPANT_COMPLIANCE_DOCS,
  URL_SP_PARTICIPANT_DOCUMENTS_LIST,
  URL_HRM_PARTICIPANT_COMPLIANCE_DOCS,
  URL_HRM_PARTICIPANT_DOCUMENTS_LIST,
} from "store/services/URL";

const RequiredDocumentsFolderCard = ({ onClick, viewMode = "grid" }) => {
  if (viewMode === "list") {
    return (
      <div
        className='border border-gray-200 shadow-sm rounded-xl bg-white hover:shadow-md transition-shadow cursor-pointer p-4'
        onClick={onClick}
        role='button'
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onClick();
        }}
      >
        <div className='flex items-center gap-4'>
          <div className='h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0'>
            <img
              src={Folderr}
              alt='Required documents folder'
              className='h-7 w-7'
            />
          </div>
          <div className='text-gray-900 font-semibold text-sm capitalize'>
            Required Documents
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className='border border-gray-200 shadow-sm rounded-xl bg-white hover:shadow-md transition-shadow cursor-pointer p-5'
      onClick={onClick}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
    >
      <div className='flex flex-col items-start'>
        <div className='flex justify-between w-full items-center mb-4'>
          <div className='h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center'>
            <img
              src={Folderr}
              alt='Required documents folder'
              className='h-8 w-8'
            />
          </div>
        </div>
        <div className='w-full'>
          <div className='text-gray-900 font-semibold text-sm leading-5 truncate w-full capitalize'>
            Required Documents
          </div>
        </div>
      </div>
    </div>
  );
};

const Documents = (props) => {
  const {
    participantInfo,
    createFolderRequest,
    updateFolderRequest,
    removeFolderRequest,
    removeFileRequest,
    moveDocumentRequest,
    getFolderListForHRMRequest,
    createNewFolderLoading,
    createNewFolderSuccess,
    removeFolderSuccess,
    FolderListForHRM,
    FileListForHRM,
    getFolderListForHRMLoading,
    DynamicBreadCrumb,
    isUserGracePeriodOver,
    refreshParticipant,
  } = props;

  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [breadCrumb, setBreadCrumb] = useState([]);
  const [isOpen, setIsopen] = useState(false);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [isOpenCreateFolder, setIsOpenCreateFolder] = useState(false);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [currentFile, setCurrentFile] = useState(null);
  const [isEditFileOpen, setIsEditFileOpen] = useState(false);
  const [parentId, setParentId] = useState(null);
  const [parentUUID, setParentUUID] = useState(null);
  /** Local visibility (etc.) patches until folder list refetches from API. */
  const [fileListPatches, setFileListPatches] = useState({});

  // Search, Filter, and Sort state
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState({ label: "Date (Newest)", value: "date_desc" });
  const [filterBy, setFilterBy] = useState({ label: "All Items", value: "all" });
  const [viewMode, setViewMode] = useState("grid");
  
  // Bulk selection state
  const [selectedItems, setSelectedItems] = useState([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // File preview state
  const [previewFile, setPreviewFile] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  
  // Metadata tooltip state - track which item's metadata is showing
  const [metadataItemId, setMetadataItemId] = useState(null);
  const [moveModal, setMoveModal] = useState(null);
  const [isBulkMoveOpen, setIsBulkMoveOpen] = useState(false);
  const [isMoveDropLoading, setIsMoveDropLoading] = useState(false);
  const [moveListRefreshAfterDrop, setMoveListRefreshAfterDrop] = useState(false);
  const sawFolderListLoadingAfterMoveRef = useRef(false);

  const [showRequiredTable, setShowRequiredTable] = useState(false);
  const [documentList, setDocumentList] = useState({});
  const [requiredDocumentslist, setRequiredDocumentslist] = useState([]);
  const [complianceDocuments, setComplianceDocuments] = useState([]);
  const [complianceDocsLoading, setComplianceDocsLoading] = useState(false);

  const hasRequiredComplianceDocuments = useMemo(() => {
    const groupedDocs = Object.values(documentList || {}).flat();
    return groupedDocs.length > 0;
  }, [documentList]);

  const requiredDocumentNames = useMemo(() => {
    const names = new Set();
    const addName = (name) => {
      if (name && typeof name === "string") {
        names.add(name.trim().toLowerCase().replace(/\./g, ""));
      }
    };

    Object.values(documentList || {})
      .flat()
      .forEach((doc) => addName(doc.name));

    (requiredDocumentslist || [])
      .filter((item) => item.type === "document")
      .forEach((item) => addName(item.name));

    return names;
  }, [documentList, requiredDocumentslist]);

  const isRequiredComplianceFile = useCallback(
    (file) => {
      const fileName = (file?.name || file?.description || file?.label || "")
        .trim()
        .toLowerCase()
        .replace(/\./g, "");
      return fileName && requiredDocumentNames.has(fileName);
    },
    [requiredDocumentNames]
  );

  const participantDocumentsListUrl = roleAccess([USER_ROLE.Sp_Standard_User])
    ? URL_HRM_PARTICIPANT_DOCUMENTS_LIST
    : URL_SP_PARTICIPANT_DOCUMENTS_LIST;
  const participantComplianceDocsUrl = roleAccess([USER_ROLE.Sp_Standard_User])
    ? URL_HRM_PARTICIPANT_COMPLIANCE_DOCS
    : URL_SP_PARTICIPANT_COMPLIANCE_DOCS;

  const getDocumentsListForProgressBar = useCallback(async () => {
    if (!participantInfo?.uuid) return;
    try {
      const res = await apiGetRequest(
        `${participantDocumentsListUrl}?participant_uuid=${participantInfo.uuid}`
      );
      if (res?.status === 200) {
        setRequiredDocumentslist(res.data.data[0] || []);
        setDocumentList(res.data.data[1] || {});
      }
    } catch (error) {
      console.error("Error fetching participant documents list:", error);
    }
  }, [participantInfo?.uuid, participantDocumentsListUrl]);

  const getComplianceDocs = useCallback(
    async (callFrom) => {
      if (!participantInfo?.uuid) return;
      try {
        if (!callFrom) {
          setComplianceDocsLoading(true);
        }
        const res = await apiGetRequest(
          `${participantComplianceDocsUrl}?participant_uuid=${participantInfo.uuid}`
        );
        if (res?.status === 200) {
          setComplianceDocuments(res.data.data || []);
        }
        await getDocumentsListForProgressBar();
        if (callFrom === "from-uploaded" && refreshParticipant) {
          refreshParticipant({ participantUUID: participantInfo.uuid });
        }
      } catch (error) {
        console.error("Error fetching participant compliance docs:", error);
      } finally {
        setComplianceDocsLoading(false);
      }
    },
    [participantInfo?.uuid, getDocumentsListForProgressBar, refreshParticipant, participantComplianceDocsUrl]
  );

  useEffect(() => {
    getComplianceDocs();
  }, [getComplianceDocs]);

  const applyFileListPatches = useCallback(
    (list) =>
      list.map((file) => {
        const patch =
          fileListPatches[file.id] ??
          (file.uuid != null ? fileListPatches[file.uuid] : undefined);
        return patch ? { ...file, ...patch } : file;
      }),
    [fileListPatches]
  );

  // Handle file permission update
  const handleFilePermissionUpdate = useCallback((updatedFile) => {
    if (!updatedFile?.id && !updatedFile?.uuid) return;
    const patch = { visibility: updatedFile.visibility };
    setFileListPatches((prev) => ({
      ...prev,
      ...(updatedFile.id != null ? { [updatedFile.id]: patch } : {}),
      ...(updatedFile.uuid != null ? { [updatedFile.uuid]: patch } : {}),
    }));
  }, []);

  useEffect(() => {
    setFileListPatches({});
  }, [FileListForHRM, parentUUID]);
  
  // Handle metadata toggle - ensures only one is open at a time
  const handleToggleMetadata = useCallback((itemId, isCurrentlyOpen) => {
    if (isCurrentlyOpen) {
      // Close if already open
      setMetadataItemId(null);
    } else {
      // Open this item's metadata (closes any other open one)
      setMetadataItemId(itemId);
    }
  }, []);
  
  // Close metadata when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (metadataItemId && !event.target.closest('[data-metadata-tooltip]')) {
        setMetadataItemId(null);
      }
    };
    
    if (metadataItemId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [metadataItemId]);

  useEffect(() => {
    if (DynamicBreadCrumb) {
      setBreadCrumb(DynamicBreadCrumb);
    }
  }, [DynamicBreadCrumb]);

  useEffect(() => {
    if (participantInfo?.uuid) {
      getFolderListForHRMRequest({
        uuid: participantInfo.uuid,
        role: "participant",
      });
      setBreadCrumb([]);
      setParentId(null);
      setParentUUID(null);
      localStorage.removeItem("breadcrumb");
    }

    return () => {
      localStorage.removeItem("breadcrumb");
    };
  }, [participantInfo?.uuid]);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  // Memoized sorted files
  const sortedFiles = useMemo(() => {
    return toPlainDocumentList(FileListForHRM).sort((a, b) => {
      switch (sortBy.value) {
        case "date_asc":
          return new Date(a.updated_at || a.created_at) - new Date(b.updated_at || b.created_at);
        case "date_desc":
          return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
        case "name_asc":
          // Sort by name first, using same field priority as display (description || name || label)
          const nameA = (a.description || a.name || a.label || "").toLowerCase();
          const nameB = (b.description || b.name || b.label || "").toLowerCase();
          if (!nameA && !nameB) {
            // Both don't have name, sort by date
            return new Date(a.updated_at || a.created_at) - new Date(b.updated_at || b.created_at);
          }
          if (!nameA) return 1; // Items without name go to end
          if (!nameB) return -1; // Items without name go to end
          return nameA.localeCompare(nameB);
        case "name_desc":
          // Sort by name first (descending), using same field priority as display
          const nameADesc = (a.description || a.name || a.label || "").toLowerCase();
          const nameBDesc = (b.description || b.name || b.label || "").toLowerCase();
          if (!nameADesc && !nameBDesc) {
            // Both don't have name, sort by date
            return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
          }
          if (!nameADesc) return 1; // Items without name go to end
          if (!nameBDesc) return -1; // Items without name go to end
          return nameBDesc.localeCompare(nameADesc);
        case "type":
          // Extract extension for file A using multiple sources (same logic as icon extraction)
          let extA = "";
          if (a.file_type) {
            extA = a.file_type.toLowerCase().trim().replace(/^\./, "");
          } else if (a.name) {
            const nameMatchA = a.name.match(/\.([a-zA-Z0-9]+)$/);
            if (nameMatchA && nameMatchA[1]) {
              extA = nameMatchA[1].toLowerCase().trim();
            }
          } else if (a.description) {
            const descMatchA = a.description.match(/\.([a-zA-Z0-9]+)$/);
            if (descMatchA && descMatchA[1]) {
              extA = descMatchA[1].toLowerCase().trim();
            }
          } else if (a.link) {
            const linkMatchA = a.link.match(/\.([a-zA-Z0-9]+)(?:[?#]|$)/);
            if (linkMatchA && linkMatchA[1]) {
              extA = linkMatchA[1].toLowerCase().trim();
            }
          }
          
          // Extract extension for file B using multiple sources
          let extB = "";
          if (b.file_type) {
            extB = b.file_type.toLowerCase().trim().replace(/^\./, "");
          } else if (b.name) {
            const nameMatchB = b.name.match(/\.([a-zA-Z0-9]+)$/);
            if (nameMatchB && nameMatchB[1]) {
              extB = nameMatchB[1].toLowerCase().trim();
            }
          } else if (b.description) {
            const descMatchB = b.description.match(/\.([a-zA-Z0-9]+)$/);
            if (descMatchB && descMatchB[1]) {
              extB = descMatchB[1].toLowerCase().trim();
            }
          } else if (b.link) {
            const linkMatchB = b.link.match(/\.([a-zA-Z0-9]+)(?:[?#]|$)/);
            if (linkMatchB && linkMatchB[1]) {
              extB = linkMatchB[1].toLowerCase().trim();
            }
          }
          
          return extA.localeCompare(extB);
        default:
          return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
      }
    });
  }, [FileListForHRM, sortBy]);

  // Memoized sorted folders
  const sortedFolders = useMemo(() => {
    return [...toPlainDocumentList(FolderListForHRM)].sort((a, b) => {
      switch (sortBy.value) {
        case "date_asc":
          return new Date(a.updated_at || a.created_at) - new Date(b.updated_at || b.created_at);
        case "date_desc":
          return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
        case "name_asc":
          // Sort by name first, if name not available then sort by date
          const folderNameA = (a.name || "").toLowerCase();
          const folderNameB = (b.name || "").toLowerCase();
          if (!folderNameA && !folderNameB) {
            // Both don't have name, sort by date
            return new Date(a.updated_at || a.created_at) - new Date(b.updated_at || b.created_at);
          }
          if (!folderNameA) return 1; // Folders without name go to end
          if (!folderNameB) return -1; // Folders without name go to end
          return folderNameA.localeCompare(folderNameB);
        case "name_desc":
          // Sort by name first (descending), if name not available then sort by date
          const folderNameADesc = (a.name || "").toLowerCase();
          const folderNameBDesc = (b.name || "").toLowerCase();
          if (!folderNameADesc && !folderNameBDesc) {
            // Both don't have name, sort by date
            return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
          }
          if (!folderNameADesc) return 1; // Folders without name go to end
          if (!folderNameBDesc) return -1; // Folders without name go to end
          return folderNameBDesc.localeCompare(folderNameADesc);
        default:
          return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
      }
    });
  }, [FolderListForHRM, sortBy]);

  const filesForFilter = useMemo(
    () => applyFileListPatches(sortedFiles),
    [sortedFiles, applyFileListPatches]
  );

  // Filter and search files
  const filteredFiles = useMemo(() => {
    let filtered = filesForFilter;

    // Apply search filter
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      filtered = filtered.filter((file) => {
        // Check all fields that may contain file name
        // This matches the display logic: item.description || item.name || item.label
        const description = (file.description || "").toLowerCase();
        const name = (file.name || "").toLowerCase();
        const label = (file.label || "").toLowerCase();
        
        // Match if search term is found in any of these fields
        return description.includes(searchLower) ||
               name.includes(searchLower) ||
               label.includes(searchLower);
      });
    }

    // Apply type filter
    if (filterBy.value === "folders") {
      return [];
    }
    if (filterBy.value === "files") {
      // Already files, continue
    } else if (filterBy.value === "expired") {
      filtered = filtered.filter((file) => isDocumentFileExpired(file));
    } else if (filterBy.value === "expiring_soon") {
      filtered = filtered.filter((file) => isDocumentFileExpiringSoon(file));
    } else if (filterBy.value === "public") {
      filtered = filtered.filter((file) => file.visibility === false);
    } else if (filterBy.value === "private") {
      filtered = filtered.filter((file) => file.visibility === true);
    }

    // Required compliance uploads belong in Required Documents only (worker pattern).
    if (breadCrumb.length === 0 && requiredDocumentNames.size > 0) {
      filtered = filtered.filter((file) => !isRequiredComplianceFile(file));
    }

    return filtered;
  }, [
    filesForFilter,
    debouncedSearch,
    filterBy,
    breadCrumb.length,
    requiredDocumentNames,
    isRequiredComplianceFile,
  ]);

  // Filter and search folders
  const filteredFolders = useMemo(() => {
    let filtered = sortedFolders;

    // Apply search filter
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      filtered = filtered.filter((folder) =>
        (folder.name || "").toLowerCase().includes(searchLower)
      );
    }

    if (filterBy.value === "files") {
      return [];
    }

    return filtered;
  }, [sortedFolders, debouncedSearch, filterBy]);

  useEffect(() => {
    setFiles(filteredFiles);
  }, [filteredFiles]);

  useEffect(() => {
    setFolders(filteredFolders);
  }, [filteredFolders]);

  const currentLevelContentsLabel = useMemo(
    () => formatFolderContentsLabel(folders.length, files.length),
    [folders.length, files.length]
  );

  useEffect(() => {
    if (isOpenCreateFolder === false) setCurrentFolder(null);
  }, [isOpenCreateFolder]);

  useEffect(() => {
    if (createNewFolderSuccess && participantInfo?.uuid) {
      getFolderListForHRMRequest({
        uuid: participantInfo.uuid,
        folder_uuid: parentUUID,
        role: "participant",
      });
    }
  }, [createNewFolderSuccess]);

  useEffect(() => {
    if (!removeFolderSuccess || !participantInfo?.uuid) return;
    getFolderListForHRMRequest({
      uuid: participantInfo.uuid,
      folder_uuid: parentUUID,
      role: "participant",
      silentRefresh: true,
    });
  }, [removeFolderSuccess]);

  const handleOnFolderClick = (item) => {
    getFolderListForHRMRequest({
      uuid: participantInfo.uuid,
      folder_uuid: item.uuid,
      role: "participant",
    });

    setParentId(item.id);
    setParentUUID(item.uuid);
    setBreadCrumb((prev) => {
      const newBreadcrumb = [...prev, item];
      localStorage.setItem("breadcrumb", JSON.stringify(newBreadcrumb));
      return newBreadcrumb;
    });
  };

  const getBreadcrumb = (e, index) => {
    setParentId(e.id);
    setParentUUID(e.uuid);
    getFolderListForHRMRequest({
      uuid: participantInfo.uuid,
      folder_uuid: e.uuid,
      role: "participant",
    });
    // Always truncate breadcrumb to the clicked level so UI reflects current path
    const newBreadcrumb = breadCrumb.slice(0, index + 1);
    setBreadCrumb(newBreadcrumb);
    if (newBreadcrumb.length > 0) {
      localStorage.setItem("breadcrumb", JSON.stringify(newBreadcrumb));
    } else {
      localStorage.removeItem("breadcrumb");
    }
  };

  const getAll = () => {
    getFolderListForHRMRequest({
      uuid: participantInfo.uuid,
      role: "participant",
    });
    setBreadCrumb([]);
    setParentId(null);
    setParentUUID(null);
    setCurrentFolder(null);
    localStorage.removeItem("breadcrumb");
  };

  const canUploadParticipantDocument = useMemo(
    () =>
      canAccessDocumentAction(
        "participant",
        DOCUMENT_PERMISSION_ACTIONS.UPLOAD
      ),
    []
  );

  const canCreateParticipantFolder = useMemo(
    () =>
      canAccessDocumentAction(
        "participant",
        DOCUMENT_PERMISSION_ACTIONS.FOLDER_CREATE
      ),
    []
  );

  const canUploadFile = useMemo(() => {
    const userType = localStorage.getItem("userType");
    return userType === "sp_standard_user";
  }, []);

  /** Frontline portal has no bulk move/delete — hide selection checkboxes. */
  const showParticipantBulkSelection = useMemo(
    () => !roleAccess([USER_ROLE.Sp_Standard_User]),
    []
  );

  useEffect(() => {
    if (!showParticipantBulkSelection) {
      setSelectedItems([]);
    }
  }, [showParticipantBulkSelection]);

  const permissionsVersion = usePermissionsVersion();

  const removeParticipantFolderRequest = useCallback(
    (payload, callback) => {
      const skipToast = payload?.skipToast;
      const cb =
        typeof callback === "function"
          ? callback
          : typeof payload?.callback === "function"
            ? payload.callback
            : null;
      if (!canParticipantDocumentFolderDelete()) {
        if (!skipToast) {
          toast.error(PARTICIPANT_DOCUMENT_FOLDER_DELETE_DENIED);
        }
        if (cb) {
          cb(null, new Error(PARTICIPANT_DOCUMENT_FOLDER_DELETE_DENIED));
        }
        return;
      }
      removeFolderRequest({
        ...payload,
        role: "participant",
        ...(cb ? { callback: cb } : {}),
      });
    },
    [removeFolderRequest, permissionsVersion]
  );

  const participantCanMoveFolder = useMemo(() => {
    if (localStorage.getItem("userType") === "sp_standard_user") {
      return false;
    }
    return roleAccess(
      [USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager],
      PERMISSION_KEYS.PARTICIPANT,
      PERMISSION_KEYS_CHILDREN.PARTICIPANT.DOCUMEMT.FOLDER.UPDATE
    );
  }, []);

  const participantCanMoveFile = useMemo(() => {
    if (localStorage.getItem("userType") === "sp_standard_user") {
      return false;
    }
    return roleAccess(
      [USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager],
      PERMISSION_KEYS.PARTICIPANT,
      PERMISSION_KEYS_CHILDREN.PARTICIPANT.DOCUMEMT.UPDATE
    );
  }, []);

  const handleOpenMoveModal = useCallback((docItem, docType) => {
    setMoveModal({ item: docItem, type: docType });
  }, []);

  const refreshFolderList = useCallback(() => {
    if (participantInfo?.uuid) {
      getFolderListForHRMRequest({
        uuid: participantInfo.uuid,
        folder_uuid: parentUUID || null,
        role: "participant",
      });
    }
  }, [getFolderListForHRMRequest, participantInfo?.uuid, parentUUID]);

  const moveDropEnabled = participantCanMoveFile || participantCanMoveFolder;

  const handleMoveDrop = useCallback(
    async ({ draggedItem, draggedType, targetFolder }) => {
      if (!participantInfo?.uuid || targetFolder?.id == null) return;
      if (draggedType === "folder" && !participantCanMoveFolder) {
        toast.error("You do not have permission to move folders.");
        return;
      }
      if (draggedType === "file" && !participantCanMoveFile) {
        toast.error("You do not have permission to move files.");
        return;
      }
      if (draggedType === "folder" && draggedItem?.id === targetFolder.id) return;

      setIsMoveDropLoading(true);
      sawFolderListLoadingAfterMoveRef.current = false;
      setMoveListRefreshAfterDrop(false);

      try {
        await executeMoveToFolder({
          item: draggedItem,
          itemType: draggedType,
          targetParentId: targetFolder.id,
          workerUuid: undefined,
          silHomeUuid: undefined,
        });
        refreshFolderList();
        setMoveListRefreshAfterDrop(true);
      } catch (e) {
        if (e?.code === MOVE_ALREADY_IN_DESTINATION) {
          toast.info("This item is already in the selected folder.");
        } else {
          const msg =
            e?.response?.data?.message ||
            e?.message ||
            (typeof e === "string" ? e : "Move failed");
          toast.error(msg);
        }
        setIsMoveDropLoading(false);
        setMoveListRefreshAfterDrop(false);
      }
    },
    [
      participantInfo?.uuid,
      participantCanMoveFolder,
      participantCanMoveFile,
      refreshFolderList,
    ]
  );

  // After a drag-move succeeds, keep overlay until folder list refetch finishes (or fallback).
  useEffect(() => {
    if (!moveListRefreshAfterDrop) {
      return undefined;
    }

    if (getFolderListForHRMLoading) {
      sawFolderListLoadingAfterMoveRef.current = true;
    } else if (sawFolderListLoadingAfterMoveRef.current) {
      sawFolderListLoadingAfterMoveRef.current = false;
      setMoveListRefreshAfterDrop(false);
      setIsMoveDropLoading(false);
      return undefined;
    }

    const fallbackMs = 1500;
    const t = setTimeout(() => {
      if (!sawFolderListLoadingAfterMoveRef.current) {
        setMoveListRefreshAfterDrop(false);
        setIsMoveDropLoading(false);
      }
    }, fallbackMs);

    return () => clearTimeout(t);
  }, [getFolderListForHRMLoading, moveListRefreshAfterDrop]);

  // Handle checkbox selection
  const handleCheckboxChange = useCallback((checked, item, type) => {
    setSelectedItems((prev) => {
      const itemId = item.uuid || (type === "folder" ? `folder-${item.id}` : `file-${item.id}`);
      if (checked) {
        // Add item with type
        return [...prev, { ...item, type, itemId }];
      } else {
        // Remove item
        return prev.filter((selected) => {
          const selectedId = selected.uuid || (selected.type === "folder" ? `folder-${selected.id}` : `file-${selected.id}`);
          return selectedId !== itemId;
        });
      }
    });
  }, []);

  // Check if item is selected
  const isItemSelected = useCallback((item, type) => {
    const itemId = item.uuid || (type === "folder" ? `folder-${item.id}` : `file-${item.id}`);
    return selectedItems.some((selected) => {
      const selectedId = selected.uuid || (selected.type === "folder" ? `folder-${selected.id}` : `file-${selected.id}`);
      return selectedId === itemId;
    });
  }, [selectedItems]);

  const canBulkDeleteFolders = useMemo(
    () => canParticipantDocumentFolderDelete(),
    [permissionsVersion]
  );

  const canBulkDeleteFiles = useMemo(
    () => canParticipantDocumentFileDelete(),
    [permissionsVersion]
  );

  const canDeleteDocuments = canBulkDeleteFolders || canBulkDeleteFiles;

  const canBulkDeleteItem = useCallback(
    (item) => {
      if (item?.type === "folder") {
        return canBulkDeleteFolders;
      }
      return canBulkDeleteFiles;
    },
    [canBulkDeleteFolders, canBulkDeleteFiles]
  );

  const selectableBulkItems = useMemo(() => {
    const items = [];
    if (canBulkDeleteFolders) {
      folders.forEach((item) => {
        items.push({
          ...item,
          type: "folder",
          itemId: item.uuid || `folder-${item.id}`,
        });
      });
    }
    if (canBulkDeleteFiles) {
      files.forEach((item) => {
        items.push({
          ...item,
          type: "file",
          itemId: item.uuid || `file-${item.id}`,
        });
      });
    }
    return items;
  }, [folders, files, canBulkDeleteFolders, canBulkDeleteFiles]);

  const handleSelectAll = useCallback(
    (checked) => {
      if (checked) {
        if (selectableBulkItems.length > 0) {
          setSelectedItems(selectableBulkItems);
          return;
        }
        const allItems = [
          ...folders.map((item) => ({
            ...item,
            type: "folder",
            itemId: item.uuid || `folder-${item.id}`,
          })),
          ...files.map((item) => ({
            ...item,
            type: "file",
            itemId: item.uuid || `file-${item.id}`,
          })),
        ];
        setSelectedItems(allItems);
      } else {
        setSelectedItems([]);
      }
    },
    [selectableBulkItems, folders, files]
  );

  const selectedDeletableCount = useMemo(
    () => selectedItems.filter((item) => canBulkDeleteItem(item)).length,
    [selectedItems, canBulkDeleteItem]
  );

  const allSelected = useMemo(() => {
    const total =
      selectableBulkItems.length > 0
        ? selectableBulkItems.length
        : folders.length + files.length;
    return total > 0 && selectedItems.length === total;
  }, [
    selectableBulkItems.length,
    folders.length,
    files.length,
    selectedItems.length,
  ]);

  // Selected items the user is allowed to move (respecting file/folder permissions)
  const selectedMovableItems = useMemo(() => {
    return selectedItems
      .filter((it) =>
        it.type === "folder" ? participantCanMoveFolder : participantCanMoveFile
      )
      .map((it) => ({ item: it, itemType: it.type }));
  }, [selectedItems, participantCanMoveFolder, participantCanMoveFile]);

  const handleBulkMoveClick = useCallback(() => {
    if (selectedMovableItems.length === 0) {
      toast.error("You do not have permission to move the selected items.");
      return;
    }
    setIsBulkMoveOpen(true);
  }, [selectedMovableItems.length]);

  // Handle bulk delete confirmation
  const handleBulkDeleteClick = useCallback(() => {
    if (!canDeleteDocuments) {
      toast.error("You do not have permission to delete documents.");
      return;
    }
    const deletableCount = selectedItems.filter((item) =>
      canBulkDeleteItem(item)
    ).length;
    if (deletableCount === 0) {
      toast.error("You do not have permission to delete the selected items.");
      return;
    }
    if (selectedItems.length === 0) {
      toast.warning("Please select items to delete.");
      return;
    }
    setShowDeleteConfirm(true);
  }, [canDeleteDocuments, canBulkDeleteItem, selectedItems]);

  // Handle bulk delete execution
  const handleBulkDelete = useCallback(async () => {
    const deletableItems = selectedItems.filter((item) =>
      canBulkDeleteItem(item)
    );
    if (deletableItems.length === 0) {
      toast.error("You do not have permission to delete the selected items.");
      setShowDeleteConfirm(false);
      return;
    }

    setIsBulkDeleting(true);
    setShowDeleteConfirm(false);
    let successCount = 0;
    let permissionDeniedCount = 0;
    let otherErrorCount = 0;

    try {
      const results = await Promise.allSettled(
        deletableItems.map((item) => {
          if (item.type === "folder") {
            const folderId = item.id || item.uuid;
            if (!folderId) {
              return Promise.reject(
                new Error(
                  `Cannot delete folder "${item.name || "Unknown"}": missing identifier`
                )
              );
            }
            return new Promise((resolve, reject) => {
              removeParticipantFolderRequest({
                folder_id: folderId,
                skipToast: true,
                callback: (res, error) => {
                  if (error) reject(error);
                  else resolve(res);
                },
              });
            });
          }
          const fileId = item.id || item.uuid;
          if (!fileId) {
            return Promise.reject(
              new Error(
                `Cannot delete file "${item.description || item.name || "Unknown"}": missing identifier`
              )
            );
          }
          return new Promise((resolve, reject) => {
            removeFileRequest({
              file_management_id: fileId,
              skipToast: true,
              callback: (res, error) => {
                if (error) reject(error);
                else resolve(res);
              },
            });
          });
        })
      );

      for (const result of results) {
        if (result.status === "fulfilled") {
          successCount++;
          continue;
        }
        console.error("Bulk delete error:", result.reason);
        const reason = result.reason;
        if (isParticipantFolderDeleteDeniedError(reason)) {
          permissionDeniedCount++;
          continue;
        }
        otherErrorCount++;
        const reasonMsg = reason?.message || "";
        if (reasonMsg.includes("missing identifier")) {
          toast.error(reasonMsg);
        }
      }

      if (successCount > 0) {
        toast.success(
          `Successfully deleted ${successCount} item${successCount !== 1 ? "s" : ""}`
        );
      }
      if (permissionDeniedCount > 0) {
        toast.error(PARTICIPANT_DOCUMENT_FOLDER_DELETE_DENIED);
      }
      if (otherErrorCount > 0) {
        toast.error(
          `Failed to delete ${otherErrorCount} item${otherErrorCount !== 1 ? "s" : ""}`
        );
      }

      setSelectedItems([]);
      
      // Refresh the list
      if (participantInfo?.uuid) {
        getFolderListForHRMRequest({
          uuid: participantInfo.uuid,
          folder_uuid: parentUUID || null,
          role: "participant",
        });
      }
    } catch (error) {
      toast.error("An error occurred during bulk delete");
    } finally {
      setIsBulkDeleting(false);
    }
  }, [
    selectedItems,
    canBulkDeleteItem,
    removeParticipantFolderRequest,
    removeFileRequest,
    participantInfo?.uuid,
    parentUUID,
    getFolderListForHRMRequest,
  ]);

  // Handle file preview
  const handleFilePreview = useCallback((fileItem) => {
    // File items from the API don't have a type property, they're just file objects
    if (fileItem && (fileItem.uuid || fileItem.id)) {
      setPreviewFile(fileItem);
      setIsPreviewOpen(true);
    }
  }, []);

  // Clear selection when filters change
  useEffect(() => {
    setSelectedItems([]);
  }, [debouncedSearch, filterBy, sortBy]);

  // Sync selection with current list: remove "ghost" selections when items are deleted
  // (e.g. single-file delete). Fixes Select All appearing checked and stale selection count.
  useEffect(() => {
    const validIds = new Set([
      ...folders.map((f) => f.uuid || `folder-${f.id}`),
      ...files.map((f) => f.uuid || `file-${f.id}`),
    ]);
    setSelectedItems((prev) => {
      const filtered = prev.filter((s) => {
        const id = s.itemId ?? s.uuid ?? (s.type === "folder" ? `folder-${s.id}` : `file-${s.id}`);
        return validIds.has(id);
      });
      return filtered.length !== prev.length ? filtered : prev;
    });
  }, [files, folders]);

  if (showRequiredTable && hasRequiredComplianceDocuments) {
    return (
      <WorkerDocument
        role='participant'
        requiredDocumentsOnly={true}
        worker={participantInfo}
        workerId={participantInfo?.id}
        documentList={documentList}
        requiredDocumentslist={requiredDocumentslist}
        documents={complianceDocuments}
        setDocuments={setComplianceDocuments}
        isLoading={complianceDocsLoading}
        getEmployeeDocuments={getComplianceDocs}
        getDocumentsListForProgressBar={getDocumentsListForProgressBar}
        onExitRequiredDocuments={() => setShowRequiredTable(false)}
        getFolderListForHRMRequest={getFolderListForHRMRequest}
        getFolderListForHRMLoading={getFolderListForHRMLoading}
        FolderListForHRM={FolderListForHRM}
        FileListForHRM={FileListForHRM}
        createFolderRequest={createFolderRequest}
        createNewFolderLoading={createNewFolderLoading}
        createNewFolderSuccess={createNewFolderSuccess}
        removeFolderRequest={removeParticipantFolderRequest}
        removeFileRequest={removeFileRequest}
        updateFolderRequest={updateFolderRequest}
      />
    );
  }

  return (
    <>
      <div className='px-4 sm:px-6 py-4 sm:py-6'>
        <div className='grid grid-cols-1 lg:grid-cols-4 gap-6'>
          {/* Main Content Area */}
          <div className='lg:col-span-3 order-2 lg:order-1'>
            {/* Action Buttons */}
            <div className='mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
              <div className='flex items-center gap-2'>
                <h2 className='text-xl font-semibold text-gray-900'>Documents</h2>
                {(folders.length > 0 || files.length > 0) && currentLevelContentsLabel && (
                  <span className='text-sm text-gray-500'>
                    ({currentLevelContentsLabel})
                  </span>
                )}
              </div>
              <div className='flex flex-col sm:flex-row gap-3'>
                {/* Create Folder Button */}
                {canCreateParticipantFolder && (
                  <CustomButton
                    label='Create Folder'
                    Icon={<BsFolderPlus />}
                    clickHandler={() => {
                      setCurrentFolder(null);
                      setIsOpenCreateFolder(true);
                    }}
                    variant='secondary'
                  />
                )}
                
                {/* Upload Document Button */}
                {canUploadParticipantDocument && (
                  <>
                    <CustomButton
                      label='Bulk Upload'
                      Icon={<BsFiles />}
                      clickHandler={() => {
                        setIsBulkOpen(true);
                      }}
                      variant='secondary'
                    />
                    <CustomButton
                      label='Upload Document'
                      Icon={<BsUpload />}
                      clickHandler={() => {
                        setIsopen(true);
                      }}
                      variant='primary'
                    />
                  </>
                )}
              </div>
            </div>

        {/* Main Content */}
        <div className='bg-white border border-gray-200 rounded-xl shadow-lg flex flex-col min-h-0 overflow-hidden'>
          {/* Search, Filter, and Sort Header */}
          <DocumentsHeader
            search={search}
            setSearch={setSearch}
            sortBy={sortBy}
            setSortBy={setSortBy}
            filterBy={filterBy}
            setFilterBy={setFilterBy}
            viewMode={viewMode}
            setViewMode={setViewMode}
            showFilters={FolderListForHRM.length > 0 || FileListForHRM.length > 0}
            hidePrivateFilter={roleAccess([USER_ROLE.Sp_Standard_User])}
          />
          {/* Select All Checkbox with Bulk Actions */}
          {showParticipantBulkSelection &&
            (folders.length > 0 || files.length > 0) && (
            <div className='px-4 sm:px-6 py-3 bg-gray-50 border-b border-gray-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3'>
              <div className='flex items-center gap-2'>
                <CheckBox
                  handleChange={handleSelectAll}
                  checked={allSelected}
                  cssClass='border-gray-500 rounded-sm'
                />
                <span className='text-sm text-gray-700 font-medium'>
                  Select All ({folders.length + files.length} items)
                </span>
                {selectedItems.length > 0 && (
                  <span className='text-sm text-gray-500 ml-2'>
                    • {selectedItems.length} selected
                  </span>
                )}
              </div>
              
              {selectedItems.length > 0 && (
                <div className='flex items-center gap-2 flex-shrink-0'>
                  <button
                    onClick={() => setSelectedItems([])}
                    className='text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors px-3 py-1.5 rounded-md hover:bg-gray-200 border border-gray-300 whitespace-nowrap'
                    disabled={isBulkDeleting}
                  >
                    Clear Selection
                  </button>
                  {selectedMovableItems.length > 0 && (
                    <CustomButton
                      label={`Move ${selectedMovableItems.length}`}
                      Icon={<FolderIcon className='h-4 w-4' />}
                      clickHandler={handleBulkMoveClick}
                      variant='secondary'
                      isDisabled={isBulkDeleting}
                      cssClass='min-w-[100px]'
                    />
                  )}
                  {canDeleteDocuments && selectedDeletableCount > 0 && (
                    <CustomButton
                      label={`Delete ${selectedDeletableCount}`}
                      Icon={<TrashIcon className='h-4 w-4' />}
                      clickHandler={handleBulkDeleteClick}
                      variant='danger'
                      showLoading={isBulkDeleting}
                      isDisabled={isBulkDeleting}
                      cssClass='min-w-[100px]'
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Breadcrumb Section */}
          {breadCrumb.length > 0 && (
            <div className='px-4 sm:px-6 py-4 bg-gray-50 border-b border-gray-200'>
              <nav>
                <ol className='flex flex-wrap items-center gap-2 sm:gap-4'>
                  <button onClick={getAll} className='cursor-pointer'>
                    <span className='text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors'>
                      All Documents
                    </span>
                  </button>
                  {breadCrumb.length > 0 && (
                    <span>
                      <ChevronRightIcon
                        className='flex-shrink-0 h-4 w-4 text-gray-400'
                        aria-hidden='true'
                      />
                    </span>
                  )}
                  {breadCrumb.length >= 4 && (
                    <>
                      <b className='mb-2'>...</b>
                      <span className='ml-0'>
                        <ChevronRightIcon
                          className='flex-shrink-0 h-4 w-4 text-gray-400'
                          aria-hidden='true'
                        />
                      </span>
                    </>
                  )}
                  {breadCrumb
                    .slice(breadCrumb.length >= 4 ? -2 : 0)
                    .map((item, index) => {
                      const actualIndex = breadCrumb.length >= 4 
                        ? breadCrumb.length - 2 + index 
                        : index;
                      const isFirstVisibleInCollapsed = breadCrumb.length >= 4 && index === 0;
                      return (
                        <BreadCrumb
                          breadCrumb={item}
                          index={actualIndex}
                          key={index}
                          handleClick={getBreadcrumb}
                          showLeadingArrow={!isFirstVisibleInCollapsed}
                        />
                      );
                    })}
                </ol>
              </nav>
            </div>
          )}

          {/* Folders and Files Grid */}
          <div className='relative custom-scroll flex-1 min-h-0 max-h-[70vh] overflow-y-auto p-4 sm:p-6'>
            {isMoveDropLoading && (
              <div
                className='absolute inset-0 z-[80] flex items-center justify-center rounded-lg bg-white/85 backdrop-blur-[1px]'
                aria-live='polite'
                aria-busy='true'
              >
                <CircularLoader classes='flex justify-center items-center' />
              </div>
            )}
            {getFolderListForHRMLoading ? (
              <DocumentSkeleton count={8} />
            ) : (
              <>
                {folders.length === 0 && files.length === 0 && !hasRequiredComplianceDocuments ? (
                  <div className='flex flex-col items-center justify-center min-h-[400px] w-full text-gray-500'>
                    <div className='mb-4'>
                      <svg
                        className='w-24 h-24 text-gray-300'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={1.5}
                          d='M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z'
                        />
                      </svg>
                    </div>
                    <p className='text-lg font-semibold text-gray-700 mb-1 text-center'>
                      No documents found!
                    </p>
                    <p className='text-sm text-gray-500 mb-4 text-center'>
                      Upload a document to get started
                    </p>
                    {canUploadParticipantDocument && (
                      <div className='flex flex-col sm:flex-row gap-2 justify-center'>
                        <CustomButton
                          label='Bulk Upload'
                          Icon={<BsFiles />}
                          clickHandler={() => setIsBulkOpen(true)}
                          variant='secondary'
                        />
                        <CustomButton
                          label='Upload Your First Document'
                          Icon={<BsUpload />}
                          clickHandler={() => setIsopen(true)}
                          variant='primary'
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {viewMode === "grid" ? (
                      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 overflow-visible'>
                        {breadCrumb.length === 0 && hasRequiredComplianceDocuments && (
                          <RequiredDocumentsFolderCard
                            onClick={() => setShowRequiredTable(true)}
                            viewMode={viewMode}
                          />
                        )}
                        {folders.length > 0 &&
                          folders.map((item) => (
                            <div key={item.uuid || `folder-${item.id}`} className='relative overflow-visible'>
                              {showParticipantBulkSelection &&
                                (canBulkDeleteFolders || participantCanMoveFolder) && (
                                <div
                                  className='absolute top-2 left-2 z-[1] bg-white rounded p-1 shadow-sm'
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <CheckBox
                                    handleChange={(checked) =>
                                      handleCheckboxChange(checked, item, "folder")
                                    }
                                    checked={isItemSelected(item, "folder")}
                                    cssClass='border-gray-500 rounded-sm'
                                  />
                                </div>
                              )}
                              <Folder
                                showFolderItemCounts
                                role="participant"
                                permissionRename={canUploadFile}
                                onClick={() => handleOnFolderClick(item)}
                                onFileClick={handleFilePreview}
                                createdDate={item.created_at}
                                type='folder'
                                badged={item.badged}
                                label={item.name}
                                item={item}
                                removeFolder={removeParticipantFolderRequest}
                                removeFile={removeFileRequest}
                                isEdit={(e) => setIsOpenCreateFolder(e)}
                                currentFolder={(e) => setCurrentFolder(e)}
                                isUserGracePeriodOver={isUserGracePeriodOver}
                                viewMode={viewMode}
                                metadataItemId={metadataItemId}
                                onToggleMetadata={handleToggleMetadata}
                                setUpdatePermission={() => {}}
                                onFilePermissionUpdate={handleFilePermissionUpdate}
                                enableMoveToFolder={participantCanMoveFolder}
                                onMoveToFolder={handleOpenMoveModal}
                                onMoveDrop={moveDropEnabled ? handleMoveDrop : undefined}
                              />
                            </div>
                          ))}
                        {files.length > 0 &&
                          files.map((item) => {
                            // Extract file extension from multiple sources to display correct icon
                            // Extension will determine which icon to show (PDF, Excel, Word, Image, etc.)
                            let ext = "";
                            
                            // First, try file_type field if available (direct field)
                            if (item.file_type) {
                              ext = item.file_type.toLowerCase().trim().replace(/^\./, "");
                            }
                            
                            // If no file_type, try extracting from name field using regex
                            if (!ext && item.name) {
                              const nameMatch = item.name.match(/\.([a-zA-Z0-9]+)$/);
                              if (nameMatch && nameMatch[1]) {
                                const potentialExt = nameMatch[1].toLowerCase().trim();
                                // Validate: extension should be 1-5 chars, not just numbers
                                if (potentialExt.length > 0 && potentialExt.length <= 5 && !/^\d+$/.test(potentialExt)) {
                                  ext = potentialExt;
                                }
                              }
                            }
                            
                            // If still no extension, try description field using regex
                            if (!ext && item.description) {
                              const descMatch = item.description.match(/\.([a-zA-Z0-9]+)$/);
                              if (descMatch && descMatch[1]) {
                                const potentialExt = descMatch[1].toLowerCase().trim();
                                // Validate: extension should be 1-5 chars, not just numbers
                                if (potentialExt.length > 0 && potentialExt.length <= 5 && !/^\d+$/.test(potentialExt)) {
                                  ext = potentialExt;
                                }
                              }
                            }
                            
                            // Last resort: try extracting from link/url if available using regex
                            if (!ext && item.link) {
                              const linkMatch = item.link.match(/\.([a-zA-Z0-9]+)(?:[?#]|$)/);
                              if (linkMatch && linkMatch[1]) {
                                const potentialExt = linkMatch[1].toLowerCase().trim();
                                // Validate: extension should be 1-5 chars, not just numbers
                                if (potentialExt.length > 0 && potentialExt.length <= 5 && !/^\d+$/.test(potentialExt)) {
                                  ext = potentialExt;
                                }
                              }
                            }
                            
                            return (
                              <div key={item.uuid || `file-${item.id}`} className='relative group overflow-visible'>
                                {showParticipantBulkSelection &&
                                  (canBulkDeleteFiles || participantCanMoveFile) && (
                                  <div
                                    className='absolute top-2 left-2 z-[1] bg-white rounded p-1 shadow-sm'
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <CheckBox
                                      handleChange={(checked) =>
                                        handleCheckboxChange(checked, item, "file")
                                      }
                                      checked={isItemSelected(item, "file")}
                                      cssClass='border-gray-500 rounded-sm'
                                    />
                                  </div>
                                )}
                                <Folder
                                  role="participant"
                                  permissionRename={canUploadFile}
                                  onFileClick={handleFilePreview}
                                  createdDate={item.created_at}
                                  type='file'
                                  badged={item.badged}
                                  label={ item.name || item.label || item.link || item.description || 'Untitled File'}
                                  item={item}
                                  ext={ext}
                                  removeFolder={removeParticipantFolderRequest}
                                  removeFile={removeFileRequest}
                                  isUserGracePeriodOver={isUserGracePeriodOver}
                                  viewMode={viewMode}
                                  metadataItemId={metadataItemId}
                                  onToggleMetadata={handleToggleMetadata}
                                  setUpdatePermission={() => {}}
                                  onFilePermissionUpdate={handleFilePermissionUpdate}
                                  isEditt={(open) => setIsEditFileOpen(open)}
                                  currentFile={(file) => setCurrentFile(file)}
                                  enableMoveToFolder={participantCanMoveFile}
                                  onMoveToFolder={handleOpenMoveModal}
                                  onMoveDrop={moveDropEnabled ? handleMoveDrop : undefined}
                                />
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <div className='space-y-3 overflow-visible'>
                        {breadCrumb.length === 0 && hasRequiredComplianceDocuments && (
                          <RequiredDocumentsFolderCard
                            onClick={() => setShowRequiredTable(true)}
                            viewMode='list'
                          />
                        )}
                        {folders.length > 0 &&
                          folders.map((item) => (
                            <div key={item.uuid || `folder-${item.id}`} className='relative overflow-visible'>
                              {showParticipantBulkSelection &&
                                (canBulkDeleteFolders || participantCanMoveFolder) && (
                                <div
                                  className='absolute left-4 top-4 z-[1] bg-white rounded p-1 shadow-sm'
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <CheckBox
                                    handleChange={(checked) =>
                                      handleCheckboxChange(checked, item, "folder")
                                    }
                                    checked={isItemSelected(item, "folder")}
                                    cssClass='border-gray-500 rounded-sm'
                                  />
                                </div>
                              )}
                              <div
                                className={
                                  showParticipantBulkSelection &&
                                  (canBulkDeleteFolders || participantCanMoveFolder)
                                    ? "pl-10"
                                    : ""
                                }
                              >
                                <Folder
                                  showFolderItemCounts
                                  role="participant"
                                  permissionRename={canUploadFile}
                                  onClick={() => handleOnFolderClick(item)}
                                  createdDate={item.created_at}
                                  type='folder'
                                  badged={item.badged}
                                  label={item.name}
                                  item={item}
                                  removeFolder={removeParticipantFolderRequest}
                                  removeFile={removeFileRequest}
                                  isEdit={(e) => setIsOpenCreateFolder(e)}
                                  currentFolder={(e) => setCurrentFolder(e)}
                                  isUserGracePeriodOver={isUserGracePeriodOver}
                                  viewMode={viewMode}
                                  metadataItemId={metadataItemId}
                                  onToggleMetadata={handleToggleMetadata}
                                  setUpdatePermission={() => {}}
                                  onFilePermissionUpdate={handleFilePermissionUpdate}
                                  enableMoveToFolder={participantCanMoveFolder}
                                  onMoveToFolder={handleOpenMoveModal}
                                  onMoveDrop={moveDropEnabled ? handleMoveDrop : undefined}
                                />
                              </div>
                            </div>
                          ))}
                        {files.length > 0 &&
                          files.map((item) => {
                            let ext = "";

                            if (item.file_type) {
                              ext = item.file_type.toLowerCase().trim().replace(/^\./, "");
                            }

                            if (!ext && item.name) {
                              const nameMatch = item.name.match(/\.([a-zA-Z0-9]+)$/);
                              if (nameMatch && nameMatch[1]) {
                                const potentialExt = nameMatch[1].toLowerCase().trim();
                                // Validate: extension should be 1-5 chars, not just numbers
                                if (potentialExt.length > 0 && potentialExt.length <= 5 && !/^\d+$/.test(potentialExt)) {
                                  ext = potentialExt;
                                }
                              }
                            }

                            // If still no extension, try description field using regex
                            if (!ext && item.description) {
                              const descMatch = item.description.match(/\.([a-zA-Z0-9]+)$/);
                              if (descMatch && descMatch[1]) {
                                const potentialExt = descMatch[1].toLowerCase().trim();
                                // Validate: extension should be 1-5 chars, not just numbers
                                if (potentialExt.length > 0 && potentialExt.length <= 5 && !/^\d+$/.test(potentialExt)) {
                                  ext = potentialExt;
                                }
                              }
                            }
                            
                            // Last resort: try extracting from link/url if available using regex
                            if (!ext && item.link) {
                              const linkMatch = item.link.match(/\.([a-zA-Z0-9]+)(?:[?#]|$)/);
                              if (linkMatch && linkMatch[1]) {
                                const potentialExt = linkMatch[1].toLowerCase().trim();
                                // Validate: extension should be 1-5 chars, not just numbers
                                if (potentialExt.length > 0 && potentialExt.length <= 5 && !/^\d+$/.test(potentialExt)) {
                                  ext = potentialExt;
                                }
                              }
                            }
                            
                            return (
                              <div key={item.uuid || `file-${item.id}`} className='relative overflow-visible'>
                                {showParticipantBulkSelection &&
                                  (canBulkDeleteFiles || participantCanMoveFile) && (
                                  <div
                                    className='absolute left-4 top-4 z-[1] bg-white rounded p-1 shadow-sm'
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <CheckBox
                                      handleChange={(checked) =>
                                        handleCheckboxChange(checked, item, "file")
                                      }
                                      checked={isItemSelected(item, "file")}
                                      cssClass='border-gray-500 rounded-sm'
                                    />
                                  </div>
                                )}
                                <div
                                  className={
                                    showParticipantBulkSelection &&
                                    (canBulkDeleteFiles || participantCanMoveFile)
                                      ? "pl-10"
                                      : ""
                                  }
                                >
                                  <Folder
                                    role="participant"
                                    permissionRename={canUploadFile}
                                    onFileClick={handleFilePreview}
                                    createdDate={item.created_at}
                                    type='file'
                                    badged={item.badged}
                                    label={item.name || item.label || item.link || item.description || 'Untitled File'}
                                    item={item}
                                    ext={ext}
                                    removeFolder={removeParticipantFolderRequest}
                                    removeFile={removeFileRequest}
                                    isUserGracePeriodOver={isUserGracePeriodOver}
                                    viewMode={viewMode}
                                    metadataItemId={metadataItemId}
                                    onToggleMetadata={handleToggleMetadata}
                                    setUpdatePermission={() => {}}
                                    onFilePermissionUpdate={handleFilePermissionUpdate}
                                    isEditt={(open) => setIsEditFileOpen(open)}
                                    currentFile={(file) => setCurrentFile(file)}
                                    enableMoveToFolder={participantCanMoveFile}
                                    onMoveToFolder={handleOpenMoveModal}
                                    onMoveDrop={moveDropEnabled ? handleMoveDrop : undefined}
                                  />
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                    {folders.length === 0 &&
                      files.length === 0 &&
                      (debouncedSearch || filterBy.value !== "all") && (
                      <div className='flex flex-col items-center justify-center min-h-[300px] text-gray-500'>
                        <p className='text-lg font-semibold text-gray-700 mb-1'>
                          No results found
                        </p>
                        <p className='text-sm text-gray-500'>
                          Try adjusting your search or filters
                        </p>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
        </div>

        {/* Recent Files Sidebar */}
        <div className='lg:col-span-1 order-1 lg:order-2 space-y-6'>
          <RecentFiles
            files={files}
            folders={folders}
            onFileClick={handleFilePreview}
            onFolderClick={handleOnFolderClick}
            maxItems={5}
          />
          
          {/* Item Metadata */}
          {metadataItemId && (() => {
            // Find the item in folders or files
            const folderItem = folders.find(f => (f.uuid || `folder-${f.id}`) === metadataItemId);
            const fileItem = files.find(f => (f.uuid || `file-${f.id}`) === metadataItemId);
            const item = folderItem || fileItem;
            const type = folderItem ? "folder" : "file";
            
            return item ? (
              <ItemMetadata
                item={item}
                type={type}
                onClose={() => setMetadataItemId(null)}
              />
            ) : null;
          })()}
        </div>
      </div>
      </div>

      {moveModal && (
        <MoveToParticipantFolderModal
          key={`${moveModal.type}-${moveModal.item?.uuid || moveModal.item?.id || "item"}`}
          open
          participantUuid={participantInfo?.uuid}
          item={moveModal.item}
          itemType={moveModal.type}
          moveDocumentRequest={moveDocumentRequest}
          onClose={() => setMoveModal(null)}
          onSuccess={refreshFolderList}
        />
      )}

      {isBulkMoveOpen && (
        <MoveToParticipantFolderModal
          open
          participantUuid={participantInfo?.uuid}
          bulkItems={selectedMovableItems}
          moveDocumentRequest={moveDocumentRequest}
          onClose={() => setIsBulkMoveOpen(false)}
          onSuccess={() => {
            refreshFolderList();
            setSelectedItems([]);
          }}
        />
      )}

      {/* Create Folder Modal */}
      {isOpenCreateFolder && (
        <CreateFolderModel
          role="participant"
          id={participantInfo.id}
          uuid={participantInfo.uuid}
          parentId={parentId}
          setParentId={setParentId}
          currentFolder={currentFolder}
          setCurrentFolder={setCurrentFolder}
          isOpen={isOpenCreateFolder}
          handleClose={setIsOpenCreateFolder}
          createFolderRequest={(payload, callback) => {
            createFolderRequest({ ...payload, role: "participant", worker_id: participantInfo.id }, callback);
          }}
          createNewFolderSuccess={createNewFolderSuccess}
          createNewFolderLoading={createNewFolderLoading}
          updateFolder={(payload, callback) => {
            updateFolderRequest(
              { ...payload, role: "participant" },
              (response, error) => {
                // Refresh folder list after successful update to ensure UI shows latest data
                if (response && !error && participantInfo?.uuid) {
                  getFolderListForHRMRequest({
                    uuid: participantInfo.uuid,
                    folder_uuid: parentUUID || null,
                    role: "participant",
                  });
                }
                
                // Call the original callback - CreateFolderModel expects (res) format
                // Pass response if successful, null/undefined if error
                if (callback) {
                  // The saga passes response.data or response directly
                  // We need to pass the folder object to the callback
                  const folderData = response?.data || response;
                  callback(folderData && !error ? folderData : null);
                }
              }
            );
          }}
          isUserGracePeriodOver={isUserGracePeriodOver || false}
        />
      )}

      {/* Upload Document Modal */}
      {isOpen && (
        <UploadNewFileModel
          role="participant"
          workerId={participantInfo.id}
          parentId={parentId}
          isOpen={isOpen}
          handleClose={(closed) => {
            setIsopen(closed);
            if (closed === false && participantInfo?.uuid) {
              getFolderListForHRMRequest({
                uuid: participantInfo.uuid,
                folder_uuid: parentUUID || null,
                role: "participant",
              });
            }
          }}
          setUpdatedFile={() => {
            if (participantInfo?.uuid) {
              getFolderListForHRMRequest({
                uuid: participantInfo.uuid,
                folder_uuid: parentUUID || null,
                role: "participant",
              });
            }
          }}
          isUserGracePeriodOver={isUserGracePeriodOver || false}
        />
      )}

      {isBulkOpen && (
        <BulkUploadDocumentModal
          role="participant"
          workerId={participantInfo.id}
          parentId={parentId}
          isOpen={isBulkOpen}
          handleClose={(closed) => {
            if (closed === false) {
              setIsBulkOpen(false);
            }
          }}
          isUserGracePeriodOver={isUserGracePeriodOver || false}
          onUploadComplete={() => {
            if (participantInfo?.uuid) {
              getFolderListForHRMRequest({
                uuid: participantInfo.uuid,
                folder_uuid: parentUUID || null,
                role: "participant",
              });
            }
          }}
        />
      )}

      {/* Bulk Actions Bar - Removed, actions now inline with Select All */}

      {/* File Preview Modal */}
      <FilePreviewModal
        isOpen={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewFile(null);
        }}
        fileItem={previewFile}
        role="participant"
        allFiles={files}
      />

      {/* Edit File Modal */}
      {isEditFileOpen && currentFile && (
        <EditModel
          role="participant"
          isOpen={isEditFileOpen}
          handleClose={(closed) => {
            setIsEditFileOpen(false);
            setCurrentFile(null);
            if (closed === false && participantInfo?.uuid) {
              getFolderListForHRMRequest({
                uuid: participantInfo.uuid,
                folder_uuid: parentUUID || null,
                role: "participant",
              });
            }
          }}
          currentFile={currentFile}
          uuid={currentFile?.uuid}
          workerId={participantInfo?.id}
          parentId={parentId}
          onModalClose={() => {
            if (participantInfo?.uuid) {
              getFolderListForHRMRequest({
                uuid: participantInfo.uuid,
                folder_uuid: parentUUID || null,
                role: "participant",
              });
            }
          }}
          isUserGracePeriodOver={isUserGracePeriodOver}
        />
      )}

      {/* Bulk Delete Confirmation Modal */}
      <ConfirmationModal
        showModal={showDeleteConfirm}
        closeModalHandler={() => {
          setShowDeleteConfirm(false);
        }}
        submitHandler={handleBulkDelete}
        title="Confirm Delete"
        heading={`Are you sure you want to delete ${selectedItems.length} item${selectedItems.length !== 1 ? "s" : ""}?`}
        isNew={true}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Delete"
        variantConfirmBtn="danger"
        confirmBtnLoader={isBulkDeleting}
      />
    </>
  );
};

export default Documents;
